// noinspection DuplicatedCode
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

const getUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || {};
  } catch (error) {
    console.error("Error parsing user from localStorage:", error);
    return {};
  }
};

const getAuthHeaders = () => {
  const user = getUser();
  return {
    Authorization: "Bearer " + user.Token,
    Accept: "*/*",
  };
};

const handleResponse = (response, thunkAPI) => {
  if (response.status === 200) {
    return response.data;
  }
  return thunkAPI.rejectWithValue(response.data);
};

const handleError = (error, thunkAPI) => {
  return thunkAPI.rejectWithValue(
    error.response?.data || { message: error.message || "Network error" }
  );
};

export const getFaceEnrollmentList = createAsyncThunk(
  "faceEnrollment/get",
  async ({ search = "", page = 1, page_size = 20 }, thunkAPI) => {
    const user = getUser();
    if (!user.Token) {
      return thunkAPI.rejectWithValue({ message: "User not authenticated" });
    }

    const params = new URLSearchParams();
    if (search) params.append("search", search);
    params.append("page", page);
    params.append("page_size", page_size);

    const url = `${process.env.REACT_APP_API_URL}hrd/GetFaceEnrollmentList?${params.toString()}`;

    try {
      const response = await axios.get(url, { headers: getAuthHeaders() });
      return { ...response.data, currentPage: page, pageSize: page_size };
    } catch (e) {
      return handleError(e, thunkAPI);
    }
  }
);

// insertFaceEnrollmentSample - descriptor dikirim sbg JSON string (128 float)
// lewat FormData field "Descriptor", BUKAN file - foto/video mentah tak
// pernah dikirim, hanya angka hasil model face-api yg sudah jalan di browser.
export const insertFaceEnrollmentSample = createAsyncThunk(
  "faceEnrollment/insertSample",
  async ({ Nik, SampleIndex, Descriptor }, thunkAPI) => {
    const user = getUser();
    if (!user?.Token) {
      return thunkAPI.rejectWithValue({ message: "User not authenticated" });
    }

    const formData = new FormData();
    formData.append("Nik", Nik);
    formData.append("SampleIndex", SampleIndex);
    formData.append("Descriptor", JSON.stringify(Descriptor));

    const url = process.env.REACT_APP_API_URL + "hrd/InsertFaceEnrollmentSample";

    try {
      const response = await axios.post(url, formData, { headers: getAuthHeaders() });
      return handleResponse(response, thunkAPI);
    } catch (e) {
      return handleError(e, thunkAPI);
    }
  }
);

// Simpan seluruh sampel sekaligus (atomik, menggantikan sampel lama). Respons
// status "Duplicate" = wajah sangat mirip karyawan lain -> tidak disimpan;
// kirim ulang dgn Force=true bila admin yakin (mis. kembar).
export const saveFaceEnrollment = createAsyncThunk(
  "faceEnrollment/save",
  async ({ Nik, Descriptors, Force = false }, thunkAPI) => {
    const user = getUser();
    if (!user?.Token) {
      return thunkAPI.rejectWithValue({ message: "User not authenticated" });
    }
    const formData = new FormData();
    formData.append("Nik", Nik);
    formData.append("Descriptors", JSON.stringify(Descriptors));
    formData.append("Force", Force ? "true" : "false");
    const url = process.env.REACT_APP_API_URL + "hrd/SaveFaceEnrollment";
    try {
      const response = await axios.post(url, formData, { headers: getAuthHeaders() });
      return handleResponse(response, thunkAPI);
    } catch (e) {
      return handleError(e, thunkAPI);
    }
  }
);

export const deleteFaceEnrollmentSample = createAsyncThunk(
  "faceEnrollment/deleteSample",
  async (id, thunkAPI) => {
    const user = getUser();
    if (!user?.Token) {
      return thunkAPI.rejectWithValue({ message: "User not authenticated" });
    }
    const formData = new FormData();
    formData.append("id", id);
    const url = process.env.REACT_APP_API_URL + "hrd/DeleteFaceEnrollmentSample";
    try {
      const response = await axios.post(url, formData, { headers: getAuthHeaders() });
      return handleResponse(response, thunkAPI);
    } catch (e) {
      return handleError(e, thunkAPI);
    }
  }
);

export const deleteFaceEnrollment = createAsyncThunk(
  "faceEnrollment/deleteAll",
  async (nik, thunkAPI) => {
    const user = getUser();
    if (!user?.Token) {
      return thunkAPI.rejectWithValue({ message: "User not authenticated" });
    }
    const formData = new FormData();
    formData.append("Nik", nik);
    const url = process.env.REACT_APP_API_URL + "hrd/DeleteFaceEnrollment";
    try {
      const response = await axios.post(url, formData, { headers: getAuthHeaders() });
      return handleResponse(response, thunkAPI);
    } catch (e) {
      return handleError(e, thunkAPI);
    }
  }
);

const initialState = {
  data: [],
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  },
  isGetFetching: false,
  isGetError: false,
  isSaveFetching: false,
  isSaveError: false,
  isDeleteFetching: false,
  message: "",
  errorMessage: "",
};

export const faceEnrollmentSlice = createSlice({
  name: "faceEnrollment",
  initialState,
  reducers: {
    clearFaceEnrollmentMessage: (state) => {
      state.message = "";
      state.errorMessage = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFaceEnrollmentList.pending, (state) => {
        state.isGetFetching = true;
        state.isGetError = false;
      })
      .addCase(getFaceEnrollmentList.fulfilled, (state, { payload }) => {
        state.isGetFetching = false;
        const d = payload.data || {};
        state.data = d.data || [];
        state.pagination = {
          currentPage: payload.currentPage || d.page || 1,
          pageSize: payload.pageSize || d.page_size || 20,
          totalCount: d.total_count || 0,
          totalPages: d.total_pages || 0,
          hasNext: d.has_next || false,
          hasPrev: d.has_prev || false,
        };
      })
      .addCase(getFaceEnrollmentList.rejected, (state, { payload }) => {
        state.isGetFetching = false;
        state.isGetError = true;
        state.errorMessage = payload?.message || "Gagal mengambil data enrollment wajah";
        state.data = [];
      })
      .addCase(insertFaceEnrollmentSample.pending, (state) => {
        state.isSaveFetching = true;
        state.isSaveError = false;
      })
      .addCase(insertFaceEnrollmentSample.fulfilled, (state, { payload }) => {
        state.isSaveFetching = false;
        state.message = payload.message;
      })
      .addCase(insertFaceEnrollmentSample.rejected, (state, { payload }) => {
        state.isSaveFetching = false;
        state.isSaveError = true;
        state.errorMessage = payload?.message || "Gagal menyimpan sampel wajah";
      })
      .addCase(deleteFaceEnrollmentSample.pending, (state) => {
        state.isDeleteFetching = true;
      })
      .addCase(deleteFaceEnrollmentSample.fulfilled, (state, { payload }) => {
        state.isDeleteFetching = false;
        state.message = payload.message;
      })
      .addCase(deleteFaceEnrollmentSample.rejected, (state, { payload }) => {
        state.isDeleteFetching = false;
        state.errorMessage = payload?.message || "Gagal menghapus sampel";
      })
      .addCase(deleteFaceEnrollment.pending, (state) => {
        state.isDeleteFetching = true;
      })
      .addCase(deleteFaceEnrollment.fulfilled, (state, { payload }) => {
        state.isDeleteFetching = false;
        state.message = payload.message;
      })
      .addCase(deleteFaceEnrollment.rejected, (state, { payload }) => {
        state.isDeleteFetching = false;
        state.errorMessage = payload?.message || "Gagal menghapus enrollment";
      });
  },
});

export const { clearFaceEnrollmentMessage } = faceEnrollmentSlice.actions;

export const faceEnrollmentSelector = (state) => {
  const s = state.faceEnrollment || {};
  return {
    data: s.data || [],
    pagination: s.pagination || initialState.pagination,
    isGetFetching: s.isGetFetching || false,
    isSaveFetching: s.isSaveFetching || false,
    isDeleteFetching: s.isDeleteFetching || false,
    message: s.message || "",
    errorMessage: s.errorMessage || "",
  };
};

export default faceEnrollmentSlice.reducer;
